import { ReactElement } from 'react';
import {
  Box,
  FormControl,
  Input,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  Autocomplete,
  TextField,
  Slider,
  Skeleton,
  FormControlLabel,
} from '@mui/material';
import { color } from '@/colors';
import { NodeType } from '@/enums/node-type.enum';
import { I18N_KEYS } from '@/language/keys';
import { Node, Edge, type BaseNodeData } from '@/types/node';
import { isIterator } from '@/utils/iterator.utils';
import { maxWeight, minWeight } from '../Views/LoraView';
import { NumberInputField } from '../../Nodes/input-fields/number-input-field/number-input-field.component';
import { ExtraSmallFontTextField } from '../../Nodes/Utils';
import LoRA from '../../Nodes/MultiLoRA/LoRA';
import { TextFieldWithCursor } from '../../Nodes/ModelNodesUtils';
import UploadFile from './DesignAppUploadFile';
import type { DesignAppInput } from '@/types/design-app.types';
import type { TFunction } from 'i18next';

const stepWeight = 0.01;

interface ValidationProps {
  error?: boolean;
  helperText?: string | null;
}

type HandleChangeFunction = (paramId: string, newValue: any) => void;

interface SeedValue {
  isRandom: boolean;
  seed: string | number;
}

interface MultiLoraValue {
  selectedLora: {
    id: string;
    name: string;
    file?: string;
    coverImage: string;
    defaultWeight: number;
    trigger: string;
    url?: string;
  } | null;
  weight: number;
}

interface PromptValue {
  prompt: string;
}

interface RenderDesignAppParamsProps {
  node?: any;
  param: any;
  handleChange: HandleChangeFunction;
  isLoading: boolean;
  validationProps: ValidationProps;
  translate: TFunction;
  disabled: boolean;
  listSelectorOptions?: (nodeId: string) => string[];
}

const getSafeNumber = (value: any): number | undefined => {
  const parsedValue = Number.parseFloat(value);
  if (isNaN(parsedValue)) {
    return undefined;
  }
  return parsedValue;
};

export const renderDesignAppParams = ({
  node,
  param,
  handleChange,
  isLoading,
  validationProps,
  translate,
  disabled,
  listSelectorOptions,
}: RenderDesignAppParamsProps): ReactElement | null => {
  switch (param.type) {
    case 'integer':
    case 'number':
      return (
        <NumberInputField
          key={`${param.id}-numberInputField`}
          inputKey={param.id}
          title={node?.data?.name || ''}
          description=""
          // @ts-expect-error - fix this
          value={getSafeNumber(param.value)}
          // @ts-expect-error - fix this
          min={getSafeNumber(node?.data?.min)}
          // @ts-expect-error - fix this
          max={getSafeNumber(node?.data?.max)}
          type={param.mode || node?.type}
          onChange={handleChange}
          disabled={disabled}
        />
      );
    case 'input': // backward compatibility 24.10 (adding separation between input of integer and floats)
      return (
        <Input
          key={`${param.id}-textfield`}
          fullWidth
          // label={key}
          value={param.value}
          size="small"
          inputProps={{ type: 'number' }}
          onChange={(e) => handleChange(param.id, parseInt(e.target.value))}
          disabled={disabled}
        />
      );
    case 'input-integer':
      return (
        <Input
          key={`${param.id}-input-integer`}
          fullWidth
          value={param.value}
          size="small"
          inputProps={{ type: 'number' }}
          onChange={(e) => handleChange(param.id, parseInt(e.target.value))}
          disabled={disabled}
        />
      );
    case 'input-number':
      return (
        <Input
          key={`${param.id}-input-float`}
          fullWidth
          value={param.value}
          size="small"
          inputProps={{ type: 'number', step: '0.1' }}
          onChange={(e) => handleChange(param.id, parseFloat(e.target.value))}
          disabled={disabled}
        />
      );
    case 'muxv2': {
      const options = node?.id && listSelectorOptions ? listSelectorOptions(node.id) : [];

      return (
        <FormControl fullWidth sx={{ mt: 1 }}>
          {/* <InputLabel id={`${key}-label`}>{property.title}</InputLabel> */}
          <Select
            labelId={`${param.id}-label`}
            id={param.id}
            value={param.value}
            onChange={(e) => handleChange(param.id, e.target.value)}
            size="small"
            disabled={disabled}
          >
            {options.map((option: any, index: number) => (
              <MenuItem key={option} value={index}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    case 'boolean':
      return (
        <Box
          key={`${param.id}-boolean`}
          sx={{
            mb: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            position: 'relative',
            ml: -1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <FormControl>
              <Checkbox
                inputProps={{ 'aria-label': 'Checkbox' }}
                checked={param.value as boolean}
                onChange={(e) => handleChange(param.id, e.target.checked)}
                disabled={disabled}
              />
            </FormControl>
          </Box>
        </Box>
      );
    case 'seed':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: -1 }}>
          <FormControl>
            <Checkbox
              inputProps={{ 'aria-label': 'Checkbox' }}
              checked={(param.value as SeedValue)?.isRandom}
              onChange={(e) =>
                handleChange(param.id, {
                  isRandom: e.target.checked,
                  seed: (param.value as SeedValue)?.seed,
                })
              }
              disabled={disabled}
            />
          </FormControl>
          <Typography className="property-title" sx={{ mr: 0.5 }}>
            Random
          </Typography>

          <Input
            value={(param.value as SeedValue)?.seed}
            inputProps={{ type: 'number' }}
            size="small"
            onChange={(e) =>
              handleChange(param.id, { isRandom: (param.value as SeedValue)?.isRandom, seed: e.target.value })
            }
            disabled={(param.value as SeedValue)?.isRandom}
            sx={{ ml: 1 }}
          />
        </Box>
      );
    case 'array':
      return (
        <>
          <ExtraSmallFontTextField
            sx={{ mt: 1 }}
            fullWidth
            id={param.id}
            multiline
            value={Array.isArray(param.value) ? param.value.join(', ') : param.value || ''}
            rows={3}
            onChange={(e) => {
              // Just pass the raw text value
              handleChange(param.id, e.target.value);
            }}
            onBlur={(e) => {
              // Only split into array when the field loses focus
              const newValue = e.target.value
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean);
              handleChange(param.id, newValue);
            }}
            size="small"
            disabled={disabled}
          />
          <Typography>Use comma for multiple entries </Typography>
        </>
      );
    case 'text':
      return <TextFieldWithCursor id={param.id} value={param.value} property={param} handleChange={handleChange} />;

    case 'multilora':
      return (
        <>
          {isLoading ? (
            <>
              <Skeleton width="100%" height={48} />
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                <Skeleton width="20%" height={18} />
                <Skeleton width="60%" height={18} />
                <Skeleton width="20%" height={18} />
              </Box>
            </>
          ) : (
            <>
              <Autocomplete
                id="lora-select"
                sx={{ width: '100%', mt: 1, backgroundColor: color.Black100 }}
                options={
                  node?.data?.loras && node.data.loras.length > 0 ? node.data.loras.filter((l: any) => l.file) : []
                }
                autoHighlight
                value={(param.value as MultiLoraValue).selectedLora}
                onChange={(event, newValue) => {
                  handleChange(node?.id || param.id, {
                    selectedLora: newValue,
                    weight: (param.value as MultiLoraValue).weight,
                  });
                }}
                size="small"
                autoComplete={false}
                getOptionLabel={(option: any) => option.name}
                getOptionKey={(option: any) => option.id}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                renderOption={(props, option: any) => {
                  const { key: _, ...optionProps } = props;
                  return (
                    <Box
                      key={option.id}
                      component="li"
                      {...optionProps}
                      sx={{
                        width: '100%',
                        backgroundColor: color.Yambo_Blue,
                        '&:hover': {
                          backgroundColor: color.Yambo_Blue_Stroke,
                        },
                      }}
                    >
                      <LoRA
                        lora={option}
                        container="node"
                        updateLora={() => {}}
                        deleteLora={() => {}}
                        setSelectedLora={() => {}}
                      />
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=""
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password', // disable autocomplete and autofill
                    }}
                    error={validationProps?.error}
                    helperText={validationProps?.helperText}
                  />
                )}
                disabled={disabled}
              />
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1 }} gap={2}>
                <FormControlLabel
                  sx={{ flexGrow: 1, ml: 0 }}
                  label={translate(I18N_KEYS.GENERAL.LORA_WEIGHT)}
                  labelPlacement="start"
                  disabled={disabled}
                  control={
                    <Slider
                      size="small"
                      value={(param.value as MultiLoraValue).weight}
                      onChange={(event, newValue) => {
                        handleChange(node?.id || param.id, {
                          weight: newValue,
                          selectedLora: (param.value as MultiLoraValue).selectedLora,
                        });
                      }}
                      aria-labelledby="weight-slider"
                      max={maxWeight}
                      min={minWeight}
                      sx={{ ml: 2 }}
                      step={stepWeight}
                      valueLabelDisplay="auto"
                      disabled={disabled}
                    />
                  }
                />
                <Input
                  value={(param.value as MultiLoraValue).weight}
                  size="small"
                  onChange={(e) =>
                    handleChange(node?.id || param.id, {
                      weight: e.target.value,
                      selectedLora: (param.value as MultiLoraValue).selectedLora,
                    })
                  }
                  inputProps={{
                    step: stepWeight,
                    min: minWeight,
                    max: maxWeight,
                    type: 'number',
                    'aria-labelledby': 'input-slider',
                    style: {
                      width: '50px',
                      fontSize: '10px',
                    },
                  }}
                  sx={{ ml: 2 }}
                  disabled={disabled}
                />
              </Box>
            </>
          )}
        </>
      );
    case 'import':
      return (
        <UploadFile
          id={param.id}
          value={param.value}
          onUpload={handleChange}
          isLoading={false}
          setIsUploading={() => {}}
          isGuest={false}
        />
      );
    case NodeType.Prompt:
      return (
        <TextField
          placeholder={translate(I18N_KEYS.SHARE_WORKFLOW_MODAL.INPUTS.PROMPT_PLACEHOLDER)}
          fullWidth
          multiline
          value={(param.value as PromptValue)?.prompt}
          onChange={(e) =>
            handleChange(param.id, {
              prompt: e.target.value,
            })
          }
          rows={4}
          inputProps={{
            style: { resize: 'vertical' },
          }}
          sx={{
            background: `${color.Black100}`,
          }}
          disabled={disabled}
        />
      );
    default:
      return null;
  }
};

/**
 * Returns the set of node IDs reachable from any WorkflowOutput node by walking
 * edges in reverse (i.e. all nodes that contribute to the output).
 */
export function getOutputConnectedNodeIds(nodes: Node[], edges: Edge[]): Set<string> {
  const connected = new Set<string>();
  const reverseAdj = new Map<string, string[]>();

  for (const edge of edges) {
    const sources = reverseAdj.get(edge.target) ?? [];
    sources.push(edge.source);
    reverseAdj.set(edge.target, sources);
  }

  function walk(id: string) {
    if (connected.has(id)) return;
    connected.add(id);
    for (const source of reverseAdj.get(id) ?? []) {
      walk(source);
    }
  }

  for (const node of nodes) {
    if (node.type === NodeType.WorkflowOutput) {
      walk(node.id);
    }
  }

  return connected;
}

/**
 * Returns leaf nodes (no incoming edges) that are connected to a WorkflowOutput.
 */
export function getWorkflowInputNodes(nodes: Node[], edges: Edge[]): Node[] {
  const connectedIds = getOutputConnectedNodeIds(nodes, edges);
  const targetIds = new Set(edges.map((e) => e.target));

  return nodes.filter((node) => connectedIds.has(node.id) && !targetIds.has(node.id));
}

function extractTextValue(value: DesignAppInput['value'] | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('prompt' in value && typeof value.prompt === 'string') return value.prompt;
    if ('string' in value && typeof value.string === 'string') return value.string;
  }
  return '';
}

/**
 * Builds synthetic inputs for delimiter-mode iterators that have incoming edges.
 * These iterators aren't shown as DesignApp inputs (their values come from the
 * graph), but the server needs the resolved options for cost calculation. We
 * split the upstream input value by the iterator's delimiter on the client side
 * since the WASM graph isn't synced with DesignApp input changes.
 */
export function getDelimiterIteratorInputs(
  nodes: Node[],
  edges: Edge[],
  allInputs: DesignAppInput[],
  inputNodeIds: Set<string>,
): { nodeId: string; input: { options: string[] }; disabled: boolean; name: string | undefined }[] {
  return nodes
    .filter(
      (n) =>
        n.type === NodeType.MuxV2 &&
        isIterator(n.data as BaseNodeData & { isIterator?: boolean }) &&
        (n.data as { delimiter?: string }).delimiter &&
        edges.some((e) => e.target === n.id) &&
        !inputNodeIds.has(n.id),
    )
    .map((n) => {
      const delimiter = (n.data as { delimiter?: string }).delimiter!;
      const sourceEdge = edges.find((e) => e.target === n.id);
      const sourceInput = allInputs.find((i) => i.id === sourceEdge?.source);
      const options = extractTextValue(sourceInput?.value)
        .split(delimiter)
        .map((v) => v.trim())
        .filter((v) => v);
      return { nodeId: n.id, input: { options }, disabled: false, name: n.data.name };
    });
}
